import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { HttpService } from '../../utility/http-service';

export interface ProdDataClientRow {
  clientId: string;
  displayName: string;
  isEnabled: boolean;
  accountId: string | null;
  extClientId: number | null;
  billingFrequency: string | null;
  msaSigned: boolean | null;
}

@Component({
  selector: 'app-prod-data',
  templateUrl: './prod-data.component.html',
  styleUrl: './prod-data.component.css',
  standalone: false
})
export class ProdDataComponent implements OnInit {
  clients: ProdDataClientRow[] = [];
  loading = false;
  saving = false;
  varianceQueuing = false;
  varianceQueued = false;
  dirtyIds = new Set<string>();
  billingOptions = ['Monthly', 'Quarterly', 'Annual'];
  searchTerm = '';

  get filteredClients(): ProdDataClientRow[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return this.clients;
    return this.clients.filter(c =>
      c.displayName.toLowerCase().includes(term) ||
      (c.accountId ?? '').toLowerCase().includes(term) ||
      (c.extClientId?.toString() ?? '').includes(term)
    );
  }

  constructor(private http: HttpService, private router: Router) {}

  ngOnInit(): void {
    this.loadClients();
  }

  loadClients(): void {
    this.loading = true;
    this.dirtyIds.clear();
    this.http.get<ProdDataClientRow[]>('ProdData/GetClients', (res: ProdDataClientRow[]) => {
      this.clients = res;
      this.loading = false;
    });
  }

  markDirty(client: ProdDataClientRow): void {
    if (client.isEnabled) this.dirtyIds.add(client.clientId);
  }

  get hasDirty(): boolean {
    return this.dirtyIds.size > 0;
  }

  saveAll(): void {
    const payload = this.clients
      .filter(c => this.dirtyIds.has(c.clientId))
      .map(c => ({
        clientId: c.clientId,
        accountId: c.accountId,
        extClientId: c.extClientId,
        billingFrequency: c.billingFrequency,
        msaSigned: c.msaSigned,
        msaSignedDate: null,
        msaSignedBy: null
      }));

    this.saving = true;
    this.http.post('ProdData/BatchUpdateClientLinks', () => {
      this.dirtyIds.clear();
      this.saving = false;
    }, null, payload);
  }

  discardChanges(): void {
    this.loadClients();
  }

  toggleEnabled(client: ProdDataClientRow): void {
    const endpoint = client.isEnabled ? 'ProdData/DisableClient' : 'ProdData/EnableClient';
    this.http.post(endpoint, () => {
      client.isEnabled = !client.isEnabled;
      if (!client.isEnabled) {
        client.accountId = null;
        client.extClientId = null;
        client.billingFrequency = null;
        client.msaSigned = null;
        this.dirtyIds.delete(client.clientId);
      }
    }, null, { clientId: client.clientId });
  }

  queueVariance(): void {
    this.varianceQueuing = true;
    this.http.post('ProdData/QueueVariance', () => {
      this.varianceQueuing = false;
      this.varianceQueued = true;
    }, null, null);
  }

  openDetail(client: ProdDataClientRow): void {
    if (client.isEnabled) {
      this.router.navigate(['/prod-data', client.clientId], {
        state: { displayName: client.displayName }
      });
    }
  }
}
